import _ from "lodash";
/**--external-- */
import React, { useRef, useMemo, useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import {
  SearchInput,
  EmptyState,
  IconButton,
  Button,
} from "@toddle-edu/ds-web";
import { NoSearchInFilterIllustration } from "@toddle-edu/ds-theme";
import { ChevronLeftOutlined, ToddleColored } from "@toddle-edu/ds-icons";

/**--internal-- */
import classNames from "classnames";
import ACLStore from "lib/aclStore";
import I18nHOC from "UIComponents/I18nHOC";
import PaginationLoader from "UIComponents/PaginationLoader";
import Loading from "UIComponents/Loading";
import { getUserDetailsWithLinkedIdentitiesQuery } from "modules/CommonQuery";
import { getUserDetailsWithLinkedIdentitiesFromCache } from "modules/CommonGraphqlHelpers";
import {
  onSelectOrganization,
  fetchMoreIdentities,
  updateIdentityScreenFilters,
  logoutUser,
  getUserInfo,
  updateSessionVerificationDetails,
  verifySessionWithServer,
  generateEmailOTPForSessionVerification,
} from "Login/modules/LoginModule";
import { ORGANIZATION_LOGO_PLACEHOLDER_URL } from "Constants";
import { useHandleScroll } from "Utils/CustomHooks";
import { prepareLoadMoreFeed } from "Utils";
import { goToBack } from "modules/NavigationModule";
import VerifyOtpModal from "AppComponents/VerifyOtpModal";
import { getPlatformUserSessionDetailsQuery } from "Platform/modules/PlatformQuery";
import { getPlatformUserSessionDetailsFromCache } from "Platform/modules/PlatformGraphqlHelpers";
import VerificationAuthModal from "routes/components/MultiFactorAuthentication/components/VerificationAuthModal";

/**--relative-- */
import classes from "./OrganizationList.scss";
import OrganizationCard from "./OrganizationCard";
import {
  getDesignation,
  getUserLinkedIdentitiesDetailsFromIdentityPool,
} from "./OrganizationListUtils";

/**
 * This component is used at 2 places:
 * 1. After sign in -> All requests will be handled by REST Api.
 * 2. On switch-organization route(By selecting switch organization from platform switcher) -> All requests will be handled by Graphql Api.
 */
const OrganizationList = props => {
  const {
    userInfo,
    t,
    fetchMoreIdentities,
    networkStatus,
    hasNextPage,
    updateIdentityScreenFilters,
    fetchIdentityUsingRestApi,
    showQueryLoader,
    isUserLoggedIn,
    goToBack,
    sessionVerificationDetails,
    updateSessionVerificationDetails,
    verifySessionWithServer,
    generateEmailOTPForSessionVerification,
    isSessionVerified,
    isGlobalHeaderPresent,
    shouldDisplayUsername,
    showMfaVerificationModal,
  } = props;

  const linkedIdentities = _.get(userInfo, "linkedIdentities", []);

  const [searchText, setSearchText] = useState("");

  const scrollContainerRef = useRef();

  const getScrollContainerRef = () => {
    return scrollContainerRef.current;
  };

  const getUserName = linkedIdentity => {
    return `${_.get(linkedIdentity, "firstName", "")} ${_.get(
      linkedIdentity,
      "lastName",
      ""
    )}`;
  };

  useEffect(() => {
    if (
      !_.isNil(isSessionVerified) &&
      !isSessionVerified &&
      sessionVerificationDetails.action !== "SWITCH_ORGANIZATION" &&
      isUserLoggedIn
    ) {
      verifyUserSession("SWITCH_ORGANIZATION");
    }
  }, [isSessionVerified]);

  /**
   * This is only applicable for SAML login. For regular login, it is not applicable.
   * **For SAML:** `isSessionVerified` can be `false`, so we need to verify the user's session.
   * **For regular login:** It will always be `true`, and the normal flow will not be interrupted.
   */
  const verifyUserSession = async action => {
    updateSessionVerificationDetails({ action });

    if (!isSessionVerified) {
      const { isSuccess } = await generateEmailOTPForSessionVerification();
      if (isSuccess) {
        updateSessionVerificationDetails({ isVisible: true });
      }
    }
  };

  const onVerifyOtpModalClose = () => {
    updateSessionVerificationDetails({ isVisible: false, otp: "" });
  };

  useHandleScroll({
    networkStatus,
    loadMoreFeed: fetchMoreIdentities,
    getScrollRef: getScrollContainerRef,
    hasNextPage,
  });

  const updateSearchTextInRedux = useMemo(() => {
    return _.debounce(
      searchText =>
        updateIdentityScreenFilters({
          data: { searchText },
          fetchIdentityUsingRestApi,
        }),
      300
    );
  }, []);

  const onSearchTextChange = value => {
    setSearchText(value);
    updateSearchTextInRedux(value);
  };

  useEffect(() => {
    // removing searchText on unMount
    return () => {
      updateIdentityScreenFilters({
        data: { searchText: "" },
      });
    };
  }, []);

  const toddleLogoTextClasses = classNames({
    [classes.toddleLogoText]: true,
    ["heading-6"]: !isGlobalHeaderPresent,
  });

  const searchContainerClasses = classNames({
    [classes.searchContainer]: true,
    [classes.searchContainerV2]: !isGlobalHeaderPresent,
  });

  const titleClasses = classNames({
    [classes.title]: true,
    [classes.titleV2]: !isGlobalHeaderPresent,
  });

  const gridContainerClasses = classNames({
    [classes.gridContainer]: true,
    [classes.gridContainerV2]: !isGlobalHeaderPresent,
  });

  return (
    <div
      className={classes.container}
      data-test-id="switchOrganization:organization-list-container:listView"
    >
      <div className={classes.header}>
        <div className={classes.headerLeftSection}>
          {isUserLoggedIn && (
            <IconButton
              variant="plain"
              icon={<ChevronLeftOutlined />}
              size="x-small"
              onClick={goToBack}
            />
          )}

          <ToddleColored size="small" />
          <h6 className={toddleLogoTextClasses}>Toddle</h6>
        </div>

        <Button size="small" onClick={logoutUser}>
          {t("common:logout")}
        </Button>
      </div>

      <div className={titleClasses}>
        {t("common:select_a_with_label_feminine", {
          label: t("common:school"),
        })}
      </div>

      <div className={searchContainerClasses}>
        {isGlobalHeaderPresent ? (
          <SearchInput
            dsVersion="2.0"
            placeholder={"Search"}
            size="large"
            value={searchText}
            onChange={onSearchTextChange}
            testId="switchOrganization:organization-list"
          />
        ) : (
          <SearchInput
            placeholder={"Search school"}
            size="large"
            value={searchText}
            onChange={onSearchTextChange}
            testId="switchOrganization:organization-list"
          />
        )}
      </div>
      {showQueryLoader && (
        <div className={classes.loaderContainer}>
          <Loading />
        </div>
      )}

      {sessionVerificationDetails.isVisible && (
        <VerifyOtpModal
          onModalClose={onVerifyOtpModalClose}
          onInputChange={updateSessionVerificationDetails}
          inputDetails={sessionVerificationDetails}
          onSubmit={verifySessionWithServer}
          generateEmailOTPForSessionVerification={
            generateEmailOTPForSessionVerification
          }
          shouldCloseOnOverlayClick={false}
          isClosable={false}
        />
      )}

      <div className={classes.scrollContainer} ref={scrollContainerRef}>
        {!showQueryLoader && (
          <div className={gridContainerClasses}>
            {_.isEmpty(linkedIdentities) && (
              <div className={classes.emptyStateContainer}>
                <EmptyState
                  illustration={NoSearchInFilterIllustration}
                  title="No school found"
                />
              </div>
            )}
            {_.map(linkedIdentities, linkedIdentity => {
              const { name: organizationName, logo, id } = _.pick(
                _.get(linkedIdentity, "organization", {}),
                ["logo", "name", "id"]
              );

              const linkedIdentityTitle = _.get(
                linkedIdentity,
                "designation.title",
                ""
              );

              const userName = getUserName(linkedIdentity);

              const onSelectOrganization = () => {
                props.onSelectOrganization(linkedIdentity);
              };

              const isAccessBlockedDueToMfa = _.get(
                linkedIdentity,
                "isAccessBlockedDueToMfa",
                false
              );

              const isMfaEnabled = _.get(linkedIdentity, "isMfaEnabled", false);
              const jwt = _.get(linkedIdentity, "jwt", null);
              const canAccessOrg =
                !isAccessBlockedDueToMfa && !(!jwt && isMfaEnabled);

              return (
                <OrganizationCard
                  isGlobalHeaderPresent={isGlobalHeaderPresent}
                  shouldDisplayUsername={shouldDisplayUsername}
                  name={organizationName}
                  logo={
                    _.isEmpty(logo) ? ORGANIZATION_LOGO_PLACEHOLDER_URL : logo
                  }
                  key={linkedIdentity?.id}
                  linkedIdentityTitle={linkedIdentityTitle}
                  onSelectOrganization={onSelectOrganization}
                  userName={userName}
                  t={t}
                  isAccessBlockedDueToMfa={isAccessBlockedDueToMfa}
                  canAccessOrg={canAccessOrg}
                />
              );
            })}
          </div>
        )}
        {showMfaVerificationModal ? (
          <VerificationAuthModal isOrgLevel={true} />
        ) : null}
        {networkStatus === 3 && <PaginationLoader />}
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { t } = ownProps;

  const identityPoolDetails = _.get(state, "login.identityPoolDetails", null);

  let userInfo = {};

  let isIdentityPaginationRequestActive = false;

  let hasNextPage = false;

  let fetchIdentityUsingRestApi = false;

  let isLoading = false;

  const searchText = _.get(state, "login.identityScreenFilters.searchText", "");

  const isUserLoggedIn = _.get(state, "login.userLoggedIn", false);

  if (!_.isEmpty(identityPoolDetails)) {
    userInfo = getUserLinkedIdentitiesDetailsFromIdentityPool({
      identityPoolDetails,
      t,
    });

    hasNextPage = _.get(identityPoolDetails, "pageInfo.hasNextPage", false);

    isIdentityPaginationRequestActive = _.get(
      identityPoolDetails,
      "isFetchingMoreData",
      false
    );

    isLoading = _.get(identityPoolDetails, "isLoading", false);

    fetchIdentityUsingRestApi = true;
  }

  const userId =
    _.get(state, "login.organizationSwitcherDetails.userId", "") ||
    _.get(getUserInfo({ portalType: "PLANNER" }), "id", "");

  const userEntityType =
    _.get(state, "login.organizationSwitcherDetails.userEntityType", "") ||
    _.get(state, "login.userInfo.userEntityType", "");

  const networkStatus = isIdentityPaginationRequestActive ? 3 : 7;

  const queryVariables = {
    id: userId,
    type: userEntityType,
    searchText,
    first: 20,
    portalType: "PLANNER",
  };

  const sessionVerificationDetails = _.get(
    state,
    "login.sessionVerificationDetails"
  );

  const isGlobalHeaderPresent = ACLStore.can("FeatureFlag:EnableGlobalHeader");

  const shouldDisplayUsername = ACLStore.can(
    "IdentityAccess:SwitchSchoolUserName"
  );

  const showMfaVerificationModal = _.get(
    state,
    "login.mfaVerificationDetails.isVisible",
    false
  );

  return {
    queryVariables,
    showQueryLoader: isLoading,
    shouldSkipQuery: !_.isEmpty(identityPoolDetails),
    userInfo,
    networkStatus,
    hasNextPage,
    fetchIdentityUsingRestApi,
    isUserLoggedIn,
    searchText,
    userId,
    userEntityType,
    sessionVerificationDetails,
    isGlobalHeaderPresent,
    shouldDisplayUsername,
    showMfaVerificationModal,
  };
};

const mapActionCreators = {
  onSelectOrganization,
  fetchMoreIdentities,
  updateIdentityScreenFilters,
  goToBack,
  updateSessionVerificationDetails,
  verifySessionWithServer,
  generateEmailOTPForSessionVerification,
};

export default compose(
  I18nHOC({ resource: ["common", "snp", "schoolSetup"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getPlatformUserSessionDetailsQuery, {
    name: "getPlatformUserSessionDetails",
    skip: ({ isUserLoggedIn }) => !isUserLoggedIn,
    options: ({ userId, userEntityType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        type: userEntityType,
      },
    }),
    props({ getPlatformUserSessionDetails }) {
      const variables = _.get(getPlatformUserSessionDetails, "variables", {});
      const data = getPlatformUserSessionDetailsFromCache(variables);

      const isSessionVerified = _.get(data, "isSessionVerified", true);

      const { networkStatus } = getPlatformUserSessionDetails;

      const isLoading = _.includes([1, 2], networkStatus);
      const showQueryLoader = isLoading && _.isEmpty(data);

      return {
        isSessionVerified,
        networkStatus,
        showQueryLoader,
      };
    },
  }),
  graphql(getUserDetailsWithLinkedIdentitiesQuery, {
    name: "getUserDetailsWithLinkedIdentities",
    alias: "getUserDetailsWithLinkedIdentities",
    skip: ({ shouldSkipQuery }) => shouldSkipQuery,
    options: ({ queryVariables }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: queryVariables,
      };
    },
    props: ({
      getUserDetailsWithLinkedIdentities,
      ownProps: { queryVariables, t },
    }) => {
      const { networkStatus } = getUserDetailsWithLinkedIdentities;

      const data = getUserDetailsWithLinkedIdentitiesFromCache(queryVariables);

      const linkedIdentitiesEdges = _.get(
        data,
        "linkedIdentityUsersV2.edgesV2",
        []
      );

      const { endCursor, hasNextPage } = _.get(
        data,
        "linkedIdentityUsersV2.pageInfo",
        {}
      );

      const linkedIdentities = _.map(linkedIdentitiesEdges, ({ node }) => {
        const { designation, type } = node;

        const finalDesignation = getDesignation({
          userType: type,
          designation,
          t,
        });

        return {
          ...node,
          designation: finalDesignation,
        };
      });

      const isLoading = _.includes([1, 2], networkStatus);

      const showQueryLoader = isLoading && _.isEmpty(data);

      return {
        showQueryLoader,
        userInfo: {
          ..._.omit(data, ["linkedIdentityUsersV2"]),
          linkedIdentities,
        },
        hasNextPage,
        networkStatus,
        fetchMoreIdentities: prepareLoadMoreFeed({
          queryData: getUserDetailsWithLinkedIdentities,
          key: "linkedIdentityUsersV2",
          variables: {
            after: endCursor,
            ...queryVariables,
          },
          edgesKey: "edgesV2",
        }),
      };
    },
  })
)(OrganizationList);
